import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { clearOnUserChange } from '../auth/user-scope';
import { CAPEX_APP_CODE } from '../models/capex.models';
import { Application, MyContext } from '../models/org.models';

/**
 * The signed-in user's context from GET /api/me/context: whether they are an administrator, where they work and
 * the applications they may open. Administrators get every application so direct links resolve, but navigation uses
 * only the ones with an active access grant. Loaded once per user and refreshed after access changes.
 */
@Injectable({ providedIn: 'root' })
export class ContextService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  readonly context = signal<MyContext | null>(null);
  readonly loading = signal(false);

  /** Admin rights from the backend once loaded, until then from the signed-in user's role. */
  readonly isAdmin = computed(() => this.context()?.isAdmin ?? this.auth.isAdmin());
  /** The applications the user is app admin of (they set those up: access, forms, workflows, Global data). */
  readonly managedAppIds = computed<string[]>(() => this.context()?.managedApplicationIds ?? []);
  /** Administrators, and app admins of at least one application, open the admin area. */
  readonly canAdminister = computed(() => this.isAdmin() || this.managedAppIds().length > 0);
  /** Every application in the context; administrators get all so direct links can resolve their pages. */
  readonly apps = computed<Application[]>(() => this.context()?.applications ?? []);
  /** Applications shown in navigation: only those the user has active app access to. */
  readonly accessibleApps = computed<Application[]>(() => {
    const context = this.context();
    if (!context) return [];
    const accessible = new Set(context.accessibleApplicationIds ?? []);
    return context.applications.filter((app) => accessible.has(app.id));
  });
  readonly places = computed(() => this.context()?.places ?? []);

  private context$?: Observable<MyContext>;

  constructor() {
    clearOnUserChange(() => {
      this.context$ = undefined;
      this.context.set(null);
    });
  }

  /** Loads the context once per user (later calls reuse it). */
  load(): Observable<MyContext | null> {
    if (!this.auth.isAuthenticated()) return of(null);

    this.context$ ??= this.http.get<MyContext>(`${environment.apiBaseUrl}/me/context`).pipe(
      tap((context) => this.context.set(context)),
      catchError((err) => {
        this.context$ = undefined; // let the next call try again
        return throwError(() => err);
      }),
      shareReplay(1)
    );

    this.loading.set(!this.context());
    return this.context$.pipe(finalize(() => this.loading.set(false)));
  }

  /** Loads the context again, e.g. after an admin changed this user's access. */
  refresh(): Observable<MyContext | null> {
    this.context$ = undefined;
    return this.load();
  }

  /** The application with this code among the ones the user may open. */
  app(code: string | null | undefined): Application | undefined {
    const wanted = code?.toUpperCase();
    return this.apps().find((a) => a.code.toUpperCase() === wanted);
  }

  /** True when the user may open the application. Administrators may open every application. */
  canOpen(code: string): boolean {
    return this.isAdmin() || !!this.app(code);
  }

  /** True when the user manages the application's Global data (its app admins; administrators manage every app). */
  canManage(code: string): boolean {
    const app = this.app(code);
    return this.isAdmin() || (!!app && (this.context()?.managedApplicationIds ?? []).includes(app.id));
  }

  /** True when the user sets up the application (by id): administrators every application, app admins theirs. */
  managesAppId(applicationId: string): boolean {
    return this.isAdmin() || this.managedAppIds().includes(applicationId);
  }

  /** Where an application's pages live: CAPEX has its own module; every other app uses the generic pages. */
  routeFor(app: Pick<Application, 'code'>): string {
    return app.code.toUpperCase() === CAPEX_APP_CODE ? '/capex' : `/apps/${encodeURIComponent(app.code)}`;
  }
}
